import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";
import { Container, Table } from "reactstrap";

import { Heading } from "components/Heading";
import { SearchInput } from "components/SearchInput";
import { Pagination } from "components/Pagination";
import { CustomModal } from "components/CustomModal";
import { CustomButton } from "components/CustomButton";
import { useLocalPaginatedRequest } from "hooks/useLocalPaginatedRequest";
import { PageHeadings, PageTitles } from "../../constants";
import {
  schoolAdminRequestsApi,
  approveSchoolAdminRequest,
  declineSchoolAdminRequest,
  revokeSchoolAdminAccess,
} from "../../api/api.service";

/**
 * People asking to administer a school on the Mind Circuit school admin portal.
 *
 * This screen is a trust boundary, not a queue to clear. Approving one of these
 * lets that person buy a plan for the school, invite its teachers, and read the
 * school's aggregate wellbeing data. Nothing upstream verifies the claim —
 * anyone with an account can request any school in the list — so the check
 * happens here or it does not happen at all.
 *
 * Which is why each row carries corroboration rather than just a name. There is
 * no stored "school domain" to check a work address against, so the server
 * answers the question from data we do hold: how many people are already
 * recorded at that school, and how many of them share this requester's email
 * domain. A request from a fresh address at a school with fifty staff on the
 * same domain reads very differently from one where nothing matches.
 *
 * The reviewer must type how they verified it. That note is stored on the audit
 * row, and requiring it is the difference between a decision and a click.
 *
 * Same flat privilege model as the rest of this console: any signed-in admin can
 * reach this screen. That is consistent with MCAP Access next door, and every
 * approval is recorded with the approver's identity.
 */

const STATUS_TABS = [
  { key: "PENDING", label: "Pending" },
  { key: "APPROVED", label: "Approved" },
  { key: "DECLINED", label: "Declined" },
  { key: "", label: "All" },
];

// Reuses the console's existing status pills rather than inventing colours.
const STATUS_CLASS = {
  PENDING: "cs-badge cs-badge-pending",
  APPROVED: "cs-badge cs-badge-active",
  DECLINED: "cs-badge cs-badge-rejected",
  REVOKED: "cs-badge cs-badge-blocked",
};

const SchoolAdminRequests = () => {
  const [status, setStatus] = useState("PENDING");
  const [review, setReview] = useState(null); // { request, mode: "approve" | "decline" | "revoke" }
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const {
    page,
    data,
    total,
    pageSize,
    isFetching,
    searchText,
    request: refetch,
    handleSearchChange,
    handlePageClick,
    onChangePageSize,
    setPageToOne,
  } = useLocalPaginatedRequest(schoolAdminRequestsApi, status ? { status } : {}, [status]);

  // Changing tab has to go back to page one, or a reviewer on page three of
  // Pending lands on an empty page three of Approved and reads it as "none".
  useEffect(() => {
    setPageToOne();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // The hook seeds `data` with "" before the first response lands.
  const requests = Array.isArray(data) ? data : [];

  const openReview = (request, mode) => {
    setReview({ request, mode });
    setNote("");
  };

  const closeReview = () => {
    if (submitting) return;
    setReview(null);
    setNote("");
  };

  const submitReview = async () => {
    if (!review) return;
    const text = note.trim();
    // Revoking takes no note: it is the undo for an approval, not a new
    // judgement about the person, and the audit row already records who did it.
    if (!text && review.mode !== "revoke") return;

    setSubmitting(true);
    try {
      if (review.mode === "revoke") {
        await revokeSchoolAdminAccess({ id: review.request.id });
        toast.success(`${review.request.userName} no longer administers their school.`);
      } else if (review.mode === "approve") {
        await approveSchoolAdminRequest({ id: review.request.id, reviewNote: text });
        toast.success(`${review.request.userName} can now administer their school.`);
      } else {
        await declineSchoolAdminRequest({ id: review.request.id, declineReason: text });
        toast.success("Request declined. They have been emailed the reason.");
      }
      setReview(null);
      setNote("");
      // Refetch rather than patching the row in place: a reviewed request leaves
      // the Pending tab entirely, so there is no row left to update.
      refetch();
    } catch (e) {
      toast.error(e?.message || "Could not save that decision.");
    } finally {
      setSubmitting(false);
    }
  };

  const orgOf = (r) => r.schoolName || r.districtName || "—";

  // Competing claims: more than one PENDING district-owner claim on the same
  // district. Approving one without seeing the other is how the wrong person gets
  // a district, so these rows say so.
  const pendingClaimCount = {};
  requests.forEach((r) => {
    if (r.districtRole && r.districtId && r.status === "PENDING") {
      pendingClaimCount[r.districtId] = (pendingClaimCount[r.districtId] || 0) + 1;
    }
  });

  return (
    <div className="page-content">
      <Helmet>
        <title>{PageTitles.SCHOOL_ADMIN_REQUESTS}</title>
      </Helmet>
      <Container fluid>
        <Heading heading={PageHeadings.SCHOOL_ADMIN_REQUESTS} />
        <p className="text-muted mb-4">
          People asking to administer a school. Approving one lets them buy a plan for that school, invite
          its teachers, and see the school&rsquo;s figures — so check the corroboration on the row before you
          do. Nothing before this screen verifies that they run the school they named.
        </p>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {STATUS_TABS.map((tab) => (
            <CustomButton
              key={tab.label}
              title={tab.label}
              color={status === tab.key ? "primary" : "light"}
              onClick={() => setStatus(tab.key)}
            />
          ))}
          <div className="ms-auto" style={{ minWidth: 260 }}>
            <SearchInput
              placeholder="Search by name, email or school"
              value={searchText}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        <div className="table-responsive">
          <Table borderless className="mb-0">
            <thead>
              <tr>
                <th>Requester</th>
                <th>School / district</th>
                <th style={{ width: 230 }}>Corroboration</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 190 }}>Decision</th>
              </tr>
            </thead>
            <tbody>
              {isFetching && (
                <tr>
                  <td colSpan={5} className="text-muted">
                    Loading…
                  </td>
                </tr>
              )}
              {!isFetching && requests.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted">
                    {searchText
                      ? `No request matches “${searchText}”.`
                      : status === "PENDING"
                      ? "Nothing waiting for review."
                      : "No requests here."}
                  </td>
                </tr>
              )}
              {!isFetching &&
                requests.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div>{r.userName || "-"}</div>
                      <div className="text-muted font-size-12">{r.userEmail}</div>
                      {r.workEmail && r.workEmail !== r.userEmail && (
                        <div className="text-muted font-size-12">work: {r.workEmail}</div>
                      )}
                    </td>
                    <td>
                      <div>{orgOf(r)}</div>
                      <div className="text-muted font-size-12">
                        {r.districtRole
                          ? "District owner claim"
                          : r.districtName && !r.schoolName
                          ? "District-wide"
                          : "Single school"}
                      </div>
                      {r.districtRole && pendingClaimCount[r.districtId] > 1 && (
                        <div className="text-danger font-size-12">
                          {pendingClaimCount[r.districtId]} pending claims on this district — compare them before
                          approving one.
                        </div>
                      )}
                    </td>
                    <td className="font-size-12">
                      {/* The three things worth comparing before approving. */}
                      <div>
                        {r.existingUsersAtSchool} already at this school
                        {r.requesterEmailDomain ? (
                          <>
                            {" · "}
                            <strong>{r.sameDomainUsersAtSchool}</strong> on @{r.requesterEmailDomain}
                          </>
                        ) : null}
                      </div>
                      <div className="text-muted">
                        {new Date(r.requestedAt).toLocaleString()}
                        {r.requestIp ? ` · ${r.requestIp}` : ""}
                      </div>
                      {r.userStatus !== "ACTIVE" && (
                        <div className="text-danger">
                          Account is {r.userStatus} — cannot be approved until they confirm their email.
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={STATUS_CLASS[r.status] || "cs-badge"}>{r.status}</span>
                    </td>
                    <td>
                      {r.status === "PENDING" ? (
                        <div className="d-flex gap-2">
                          <CustomButton
                            title="Approve"
                            color="primary"
                            disabled={r.userStatus !== "ACTIVE"}
                            onClick={() => openReview(r, "approve")}
                          />
                          <CustomButton title="Decline" color="light" onClick={() => openReview(r, "decline")} />
                        </div>
                      ) : r.status === "APPROVED" ? (
                        <div className="d-flex flex-column gap-1">
                          <div className="text-muted font-size-12">{r.reviewNote || "—"}</div>
                          <div>
                            <CustomButton
                              title="Revoke"
                              color="light"
                              onClick={() => openReview(r, "revoke")}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="text-muted font-size-12">
                          {r.reviewNote || r.declineReason || "—"}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>

        {!!total && (
          <Pagination
            currentPage={page}
            totalData={total}
            rowLimit={pageSize}
            onPageClick={handlePageClick}
            handleChange={onChangePageSize}
          />
        )}

        <CustomModal isOpen={!!review} onClose={closeReview} toggle={closeReview} size="md">
          {review && (
            <div className="px-3 pb-3">
              <h5 className="mb-2">
                {review.mode === "approve" ? "Approve" : review.mode === "revoke" ? "Revoke" : "Decline"}{" "}
                {review.request.userName}
              </h5>
              <p className="text-muted font-size-13">
                {review.mode === "revoke" ? (
                  <>
                    They will immediately lose access to <strong>{orgOf(review.request)}</strong>&rsquo;s
                    dashboard and figures. Their Mind Circuit account and their school&rsquo;s plan are not
                    affected, and they can be approved again later.
                  </>
                ) : review.mode === "approve" ? (
                  <>
                    They will be able to buy a plan for <strong>{orgOf(review.request)}</strong>, invite its
                    teachers, and read its figures. Record how you confirmed they run this school.
                  </>
                ) : (
                  <>
                    This reason is emailed to them word for word, so write it for them to read. They can
                    apply again afterwards.
                  </>
                )}
              </p>

              {review.mode !== "revoke" && (
              <textarea
                className="form-control mb-3"
                rows={3}
                autoFocus
                maxLength={500}
                value={note}
                placeholder={
                  review.mode === "approve"
                    ? "e.g. Called the school office and confirmed with the front desk"
                    : "e.g. We could not confirm your role at this school"
                }
                onChange={(e) => setNote(e.target.value)}
              />
              )}

              <div className="d-flex justify-content-end gap-2">
                <CustomButton title="Cancel" color="light" onClick={closeReview} disabled={submitting} />
                <CustomButton
                  title={
                    review.mode === "approve" ? "Approve" : review.mode === "revoke" ? "Revoke" : "Decline"
                  }
                  color={review.mode === "approve" ? "primary" : "danger"}
                  loading={submitting}
                  disabled={review.mode !== "revoke" && !note.trim()}
                  onClick={submitReview}
                />
              </div>
            </div>
          )}
        </CustomModal>
      </Container>
    </div>
  );
};

export default SchoolAdminRequests;
